import momentTz from 'moment-timezone';
import { ICalendarEvent } from 'model/calendar/events';

/**
 * Collapses a multi-week span of recurring occurrences into one standing week —
 * the provider's template rather than a calendar.
 *
 * Each series contributes exactly one slot. When the series already runs in the
 * displayed week that occurrence is used as-is. When it starts later, its first
 * occurrence is projected onto the same weekday and time of the displayed week
 * and tagged with `seriesStartDate`, so the slot reads as taken and can be
 * flagged as not yet started.
 */

/** Occurrences of one series share a masterId; a lone event is its own series. */
const seriesKeyOf = (event: ICalendarEvent): string =>
  String(event.masterId ?? event.id);

/**
 * The event's real moment, as stored.
 *
 * `start` is a drawn position: on this view it carries the provider's wall clock
 * rather than the viewer's (AXON-3603), so it is the right thing to project onto
 * a week and the wrong thing to compare against a server value. The dates this
 * file hands back — `seriesStartDate`, `seriesEndDate` — are converted with the
 * clinic timezone by their formatters, so giving them a drawn position would
 * apply that conversion twice.
 */
const instantOf = (event: ICalendarEvent): Date =>
  new Date((event.trueStart ?? event.start) as Date);

const startOfWeek = (date: Date) => momentTz(date).startOf('week');

/** Same weekday and clock time, moved into the given week. */
const projectOntoWeek = (date: Date, weekStart: Date): Date => {
  const source = momentTz(date);
  return startOfWeek(weekStart)
    .add(source.day(), 'days')
    .hour(source.hour())
    .minute(source.minute())
    .second(0)
    .millisecond(0)
    .toDate();
};

const byStartAscending = (a: ICalendarEvent, b: ICalendarEvent) =>
  instantOf(a).getTime() - instantOf(b).getTime();

export const buildTemplateSeries = (
  events: ICalendarEvent[],
  weekStart: Date
): ICalendarEvent[] => {
  const weekBegin = startOfWeek(weekStart);
  const weekEnd = weekBegin.clone().endOf('week');

  const bySeries = new Map<string, ICalendarEvent[]>();
  events.forEach(event => {
    if (!event.start) return;
    const key = seriesKeyOf(event);
    const existing = bySeries.get(key);
    if (existing) existing.push(event);
    else bySeries.set(key, [event]);
  });

  const template: ICalendarEvent[] = [];
  bySeries.forEach(occurrences => {
    const sorted = [...occurrences].sort(byStartAscending);

    const thisWeek = sorted.find(event => {
      const start = momentTz(event.start);
      return start.isSameOrAfter(weekBegin) && start.isSameOrBefore(weekEnd);
    });
    if (thisWeek) {
      template.push(thisWeek);
      return;
    }

    const first = sorted[0];
    const start = projectOntoWeek(new Date(first.start!), weekStart);
    const durationMs = first.end
      ? new Date(first.end).getTime() - new Date(first.start!).getTime()
      : 0;
    // Only the series' own first occurrence (instanceIndex 0) proves when it
    // begins. A later occurrence just means this week had none — a fortnightly
    // cadence, or a gap — so the slot is shown without a "Starts" date rather
    // than claiming a long-running series is about to start.
    const isSeriesOpening = first.instanceIndex === 0;
    template.push({
      ...first,
      start,
      end: new Date(start.getTime() + durationMs),
      seriesStartDate: isSeriesOpening ? instantOf(first) : undefined
    });
  });

  return template;
};
